import {
  ACTIVITY_STATUS,
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  FormFeedBack,
  NoDataComponent,
  PageWrapper,
  Select,
  STATUSPAGE,
  SUBMIT_BUTTON,
  TableVirtualized,
  TableWithGroupingsLoadingSkeleton,
} from '@repo/ui';
import noData from 'presentation/app/assets/images/no-task.png';
import { makeActivitiesAnalyticalPerGroupColumns } from 'presentation/app/modules/Reports/common/columns/activities/activities-analytical-per-group-columns';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useActivitiesAnalyticalPerGroup } from 'presentation/app/modules/Reports/hooks/useActivitiesAnalyticalPerGroup';
import { Col, Card, Input, Label, Spinner, FormGroup, Row } from 'reactstrap';
import { v4 as uuid } from 'uuid';

export const ActivitiesAnalyticalPerGroup = () => {
  const {
    // States
    loading,
    validation,
    groupsListData,
    hasFetchedData,
    selectedSubmitBtnRef,
    // Methods
  } = useActivitiesAnalyticalPerGroup();

  const formattedData = groupsListData?.reduce(
    (acc, group) => [
      ...acc,
      {
        id: uuid(),
        isHeader: true,
        title: (
          <div className="d-flex justify-content-between">
            <span>{group?.group}</span> <span>{group?.course}</span>
          </div>
        ),
      },
      ...group?.activities?.map((activity) => {
        return {
          id: uuid(),
          ...activity,
          hasFooter: !!activity?.feedback,
          footerText: activity?.feedback
            ? `Feedback: ${activity?.feedback}`
            : null,
        };
      }),
    ],
    [],
  );

  return (
    <PageWrapper title="Analítico">
      <Col className="mb-3 border-0 d-flex flex-column">
        <h4>Analítico</h4>
        <p className="text-muted mb-0">
          Consulte o relatório analítico detalhado de atividades por grupo.
        </p>
      </Col>
      <Card>
        <ReportsGeneralFilters
          buttonsChildren={
            <div className="w-100 d-flex justify-content-end gap-2">
              <button
                id="search-button-json"
                type="submit"
                onClick={() => {
                  selectedSubmitBtnRef.current = SUBMIT_BUTTON.JSON;
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={
                  loading.loadingButtons === STATUSPAGE.LOADING &&
                  selectedSubmitBtnRef.current === SUBMIT_BUTTON.JSON
                }
              >
                {selectedSubmitBtnRef.current === SUBMIT_BUTTON.JSON &&
                loading.loadingButtons === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>
              <button
                type="submit"
                id="export-pdf"
                onClick={() => {
                  selectedSubmitBtnRef.current = SUBMIT_BUTTON.PDF;
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={
                  loading.loadingButtons === STATUSPAGE.LOADING &&
                  selectedSubmitBtnRef.current === SUBMIT_BUTTON.PDF
                }
              >
                {selectedSubmitBtnRef.current === SUBMIT_BUTTON.PDF &&
                loading.loadingButtons === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="export-excel"
                type="submit"
                onClick={() => {
                  selectedSubmitBtnRef.current = SUBMIT_BUTTON.EXCEL;
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={
                  loading.loadingButtons === STATUSPAGE.LOADING &&
                  selectedSubmitBtnRef.current === SUBMIT_BUTTON.EXCEL
                }
              >
                {selectedSubmitBtnRef.current === SUBMIT_BUTTON.EXCEL &&
                loading.loadingButtons === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
          validation={validation}
          inputsChildren={
            <>
              <Col lg={4} className="mb-3">
                <Label htmlFor="activityStatus" className="form-label">
                  Situação da Atividade:
                </Label>
                <Select
                  isMulti
                  isClearable
                  id="activityStatus"
                  name="activityStatus"
                  placeholder="Todas as situações"
                  value={validation.values.activityStatus as any}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('activityStatus', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={[
                    { label: 'Agendadas', value: ACTIVITY_STATUS.SCHEDULED },
                    { label: 'Realizadas', value: ACTIVITY_STATUS.FINISHED },
                    { label: 'Canceladas', value: ACTIVITY_STATUS.CANCELED },
                    { label: 'Perdidas', value: ACTIVITY_STATUS.MISSED },
                    {
                      label: 'Perdidas Justificadas',
                      value: ACTIVITY_STATUS.MISSED_JUSTIFIED,
                    },
                  ]}
                />
                <FormFeedBack
                  error={validation?.errors?.activityStatus as any}
                  isInvalid={
                    validation?.touched &&
                    (validation?.errors?.activityStatus as any)
                  }
                />
              </Col>

              <Col sm={12} md={6} lg={3} className="mb-3">
                <Row>
                  <Label htmlFor="initial-date" className="form-label">
                    Período
                  </Label>
                  <CustomDateRangePicker
                    onDateChange={(start, ends) => {
                      validation.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validation.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />
                  <FormFeedBack
                    error={validation?.errors?.starts as any}
                    isInvalid={
                      validation?.touched && (validation?.errors?.starts as any)
                    }
                  />
                </Row>
              </Col>

              <Col sm={12} lg={6}>
                <Label htmlFor="validation" className="form-label">
                  Validação:
                </Label>

                <div className="d-flex flex-wrap gap-4">
                  <FormGroup>
                    <Input
                      name="validation"
                      id="all"
                      type="radio"
                      value={null}
                      checked={!validation.values.validation}
                      onChange={(e) => {
                        if (e.target.checked) {
                          validation.handleChange({
                            target: {
                              name: 'validation',
                              value: null,
                            },
                          });
                        }
                      }}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="all" className="ms-1 form-check-label">
                      Todos
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="validated"
                      type="radio"
                      value="1"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="validated"
                      className="ms-1 form-check-label"
                    >
                      Validadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="not-validated"
                      type="radio"
                      value="2"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="not-validated"
                      className="ms-1 form-check-label"
                      onChange={validation.handleChange}
                    >
                      Rejeitadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="rejected"
                      type="radio"
                      value="3"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="rejected" className="ms-1 form-check-label">
                      Pendente
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </>
          }
        />

        {groupsListData?.length === 0 &&
          hasFetchedData &&
          loading.loadingTable === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {loading.loadingTable === STATUSPAGE.LOADING && (
          <TableWithGroupingsLoadingSkeleton />
        )}
        {loading.loadingTable === STATUSPAGE.DONE &&
          groupsListData.length > 0 && (
            <TableVirtualized
              columns={makeActivitiesAnalyticalPerGroupColumns()}
              data={formattedData}
            />
          )}
      </Card>
    </PageWrapper>
  );
};
